// El cartel de confirmacion para anular un pedido.
//
// Vive aca porque lo abren dos pantallas —Pedidos y Cuentas— y tiene que decir
// EXACTAMENTE lo mismo en las dos: es la ultima pantalla antes de una accion que
// no se puede deshacer, y una de las dos versiones quedaria vieja el dia que se
// cambie el texto.

import { api } from "./api";
import { formatearPesos } from "../lib/money";
import { nombreTurno } from "../lib/types";
import type { Turno } from "../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

function ddmmaaaa(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

/** Lo que hay que mostrar antes de anular. Lo arma cada pantalla con lo que ya tiene. */
export interface PedidoAAnular {
  id: number;
  cliente: string;
  fecha_entrega: string | null;
  turno: Turno | null;
  total: number;
}

/**
 * Pide confirmacion y anula. `alAnular` se llama solo si el servidor acepto,
 * para que la pantalla se refresque; si falla, el error queda en el cartel y no
 * se toca nada.
 */
export function abrirAnular(
  dlg: HTMLDialogElement,
  pedido: PedidoAAnular,
  alAnular: () => Promise<void>,
): void {
  dlg.innerHTML = `
    <div class="tarjeta w-[26rem] max-w-full border-0 shadow-none">
      <h2 class="mb-2 text-lg font-bold text-red-700">Anular el pedido #${esc(pedido.id)}</h2>
      <div class="mb-3 space-y-0.5 text-sm text-masa-700">
        <div><span class="text-masa-500">Cliente:</span> ${esc(pedido.cliente)}</div>
        ${
          pedido.fecha_entrega
            ? `<div><span class="text-masa-500">Entrega:</span> ${ddmmaaaa(pedido.fecha_entrega)}${
                pedido.turno ? ` — ${esc(nombreTurno(pedido.turno))}` : ""
              }</div>`
            : ""
        }
        <div><span class="text-masa-500">Total:</span> <b>${formatearPesos(pedido.total)}</b></div>
      </div>
      <p class="text-sm text-masa-600">
        El pedido sale de la <b>producción del día</b> y al local le llega un
        <b>aviso al celular</b>. En la cuenta <b>no se borra nada</b>: la compra queda y debajo
        se asienta la anulación que la cancela, así el saldo vuelve a lo que era y en el estado
        de cuenta se ve qué pasó. No se puede volver atrás: si fue un error, hay que cargar el
        pedido de nuevo.
      </p>
      <label class="mt-3 block"><span class="etiqueta">Motivo (opcional)</span>
        <input id="an_motivo" class="campo" placeholder="Se cargó con el cliente equivocado" /></label>
      <p id="an_err" class="mt-2 text-sm text-red-600"></p>
      <div class="mt-4 flex justify-end gap-2">
        <button id="an_no" class="btn-sec">Volver</button>
        <button id="an_si" class="btn-peligro">Anular el pedido</button>
      </div>
    </div>`;

  const anErr = dlg.querySelector("#an_err") as HTMLElement;
  const btnSi = dlg.querySelector("#an_si") as HTMLButtonElement;
  dlg.querySelector("#an_no")?.addEventListener("click", () => dlg.close());
  btnSi.addEventListener("click", async () => {
    const motivo = (dlg.querySelector("#an_motivo") as HTMLInputElement).value.trim();
    btnSi.disabled = true;
    anErr.textContent = "";
    try {
      await api.del(`/api/pedidos/${pedido.id}`, motivo ? { motivo } : undefined);
      dlg.close();
      await alAnular();
    } catch (e) {
      btnSi.disabled = false;
      anErr.textContent = e instanceof Error ? e.message : "Error";
    }
  });
  dlg.showModal();
}
